import { LoadingButton } from "@mui/lab";
import { Box, Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import { useState } from "react";

import { P } from "theme/typography";
import { CollectPermission, CollectUserView } from "generated/graphql";
import PCheckbox from "components/PCheckbox";
import { setCollectUserPermissions } from "redux/slices/client";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { ALL_COLLECT_PERMISSIONS } from "./ALL_COLLECT_PERMISSIONS";

interface Props {
  open: boolean;
  onClose: () => void;
  user: CollectUserView;
}

const CollectUserPermissionPicker = ({ open, onClose, user }: Props) => {
  const [loading, setLoading] = useState(false);
  const snackpatch = useAppDispatchWithSnackbar();

  const [selectedPermissions, setSelectedPermissions] = useState<CollectPermission[]>(user.permissions ?? []);

  const handleSave = () => {
    setLoading(true);

    void snackpatch(
      setCollectUserPermissions({
        collectUserId: user.id,
        permissions: ALL_COLLECT_PERMISSIONS.map((permission) => ({
          permission: permission.value,
          status: selectedPermissions.includes(permission.value),
        })),
      }),
      {
        successMessage: "User permission updated",
        errorMessage: "Unable to update user permissions",
      }
    ).finally(() => {
      onClose();
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent>
        <P variant="h5">User Permissions</P>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "400px",
            mt: 2,
          }}>
          <P
            sx={{
              mb: 1,
            }}>
            Select permissions to grant to this user
          </P>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              maxHeight: "300px",
              overflow: "auto",
              pl: 2,
            }}>
            <Box>
              {ALL_COLLECT_PERMISSIONS.map((permission) => {
                return (
                  <Box key={permission.value}>
                    <PCheckbox
                      checked={selectedPermissions.includes(permission.value)}
                      label={permission.name}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setSelectedPermissions([...selectedPermissions, permission.value]);
                        } else {
                          setSelectedPermissions(selectedPermissions.filter((p) => p !== permission.value));
                        }
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton loading={loading} onClick={handleSave} variant="contained">
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CollectUserPermissionPicker;
